export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    timeZone: "UTC",
    day: "numeric",
    month: "numeric",
    year: "2-digit",
  });
};

export const getTemplateType = (type: any) => {
  switch (type) {
    case "1":
      return "FTD";
    case "2":
      return "Growth";
    case "3":
      return "Profit";
    default:
      return "N/A";
  }
};

export interface NonEditableMonth {
  displayMonth: string;
  month: string;
  spend: number;
  clicks: number;
  cpc: number;
  ftd: number;
  cvr: number;
  cac: number;
  profit: number;
  cpa: number;
  rs: number;
  total_income: number;
  cpa_pv: number;
  rs_pv: number;
  pv: number;
  growth_target: number;
  growth: number;
  active_users: number;
  old_active_users: number;
  acquired_active_users: number;
  churn_rate: number;
  ratio_between_acquired_to_ftd: number;
  avg_net_rev_per_active_user: number;
  net_revenue: number;
  avg_rs_deal_p: number;
  br_to_ng_active_rs_ratio: number;
  epc_cpa: number;
  epc_rs: number;
  epc: number;
  estNetRev: number;
  estRs: number;
  estCpaIncome: number;
}

export const getPreviousMonthActiveUsers = (
  currentMonth: string,
  monthNames: string[],
  selectedYear: string,
  monthlyData: { [key: string]: any },
  historicalData: any[],
) => {
  const monthIndex = monthNames.indexOf(currentMonth);

  // For January, use December of previous year if available
  if (monthIndex === 0) {
    const decemberData = historicalData.find((entry) => {
      const [year, month] = entry.month.split("-").map(Number);
      return month === 12 && year === Number(selectedYear) - 1;
    });
    return decemberData?.active_users || 0;
  }

  // For other months, use previous month's data
  const prevMonthName = monthNames[monthIndex - 1];

  // If previous month is in our forecast data, use that
  if (monthlyData[prevMonthName]) {
    // First check for activeUsers (calculated value)
    if (monthlyData[prevMonthName].activeUsers !== undefined) {
      return monthlyData[prevMonthName].activeUsers;
    }
    // Then check for active_users (historical data format)
    if (monthlyData[prevMonthName].active_users !== undefined) {
      return monthlyData[prevMonthName].active_users;
    }
  }

  // Otherwise check historical data
  const prevMonthNum = monthIndex; // Convert to 1-based index
  const prevMonthData = historicalData.find((entry) => {
    const [year, month] = entry.month.split("-").map(Number);
    return month === prevMonthNum && year === Number(selectedYear);
  });

  return prevMonthData?.active_users || 0;
};

// Maps calculateInitialValues()'s camelCase shape onto NonEditableMonth's snake_case shape
export const mapForecastEstimateToNonEditableRow = (
  estimate: any,
  displayMonth: string,
  monthStr: string,
): NonEditableMonth => {
  return {
    displayMonth,
    month: monthStr,
    spend: estimate?.spend ?? 0,
    clicks: estimate?.clicks ?? 0,
    nrpu: estimate?.nrpu ?? 0,
    cpc: estimate?.cpc ?? 0,
    ftd: estimate?.ftd ?? 0,
    cvr: estimate?.cvr ?? 0,
    cac: estimate?.cac ?? 0,
    profit: estimate?.profit ?? 0,
    cpa: estimate?.estCpaIncome ?? 0,
    rs: estimate?.estRs ?? 0,
    total_income: estimate?.totalIncome ?? 0,
    cpa_pv: estimate?.cpaPv ?? 0,
    rs_pv: estimate?.rsPv ?? 0,
    pv: (estimate?.cpaPv ?? 0) + (estimate?.rsPv ?? 0),
    growth_target: estimate?.growth ?? 0,
    growth: estimate?.growth ?? 0,
    active_users: estimate?.activeUsers ?? 0,
    old_active_users: estimate?.oldActiveUsers ?? 0,
    acquired_active_users: estimate?.acquiredUsers ?? 0,
    churn_rate: estimate?.churnRate ?? 0,
    ratio_between_acquired_to_ftd: estimate?.ratioBetweenAcquiredToFtd ?? 0,
    avg_net_rev_per_active_user: estimate?.avgNetRevPerActiveUser ?? 0,
    net_revenue: estimate?.estNetRev ?? 0,
    avg_rs_deal_p: estimate?.avgRsP ?? 0,
    br_to_ng_active_rs_ratio: estimate?.rsBrToRsActiveRatio ?? 0,
    epc_cpa: estimate?.epcCpa ?? 0,
    epc_rs: estimate?.epcRs ?? 0,
    epc: estimate?.epc ?? 0,
    estNetRev: estimate?.estNetRev ?? 0,
    estRs: estimate?.estRs ?? 0,
    estCpaIncome: estimate?.estCpaIncome ?? 0,
  } as NonEditableMonth;
};

// True for the current month or any future month — targets/published
// forecasts only apply to months that already happened.
const isCurrentOrFutureMonth = (monthStr: string): boolean => {
  if (!monthStr) return false;
  const [year, month] = monthStr.split("-").map(Number);
  if (isNaN(year) || isNaN(month)) return false;
  const today = new Date();
  const currentYear = today.getFullYear();
  if (year > currentYear) return true;
  if (year < currentYear) return false;
  const monthEndDate = new Date(year, month, 0);
  return today <= monthEndDate;
};

export const calculateNonEditableMonths = (
  RequiredDataLastMonths: any[],
  monthlyData: { [key: string]: any },
  monthNames: string[],
  selectedYear: string,
  forecastEstimates?: { [key: string]: any },
): NonEditableMonth[] => {
  if (!RequiredDataLastMonths || Object.keys(monthlyData).length === 0) {
    return [];
  }

  // Only use target data for months that have already elapsed
  let mappedMonths = RequiredDataLastMonths.filter(
    (row: any) => !isCurrentOrFutureMonth(row?.month),
  ).map((row: any) => {
    const [, month] = row?.month.split("-").map(Number);
    const monthName = monthNames[month - 1];
    return { ...row, displayMonth: monthName };
  });

  // Find missing months
  const monthlyDataMonths = new Set(Object.keys(monthlyData));
  const existingMonths = new Set(mappedMonths.map((m: any) => m.displayMonth));
  const missingMonths = monthNames.filter(
    (month) => !existingMonths.has(month) && !monthlyDataMonths.has(month),
  );

  // Create data for missing months: prefer a computed forecast estimate over zeros
  const defaultMonths = missingMonths.map((monthName) => {
    const monthIndex = monthNames.indexOf(monthName) + 1;
    const monthStr = `${selectedYear}-${monthIndex.toString().padStart(2, "0")}`;
    const estimate = forecastEstimates?.[monthName];
    if (estimate) {
      return mapForecastEstimateToNonEditableRow(estimate, monthName, monthStr);
    }
    return {
      displayMonth: monthName,
      month: monthStr,
      spend: 0,
      clicks: 0,
      nrpu: 0,
      cpc: 0,
      ftd: 0,
      cvr: 0,
      cac: 0,
      profit: 0,
      cpa: 0,
      rs: 0,
      total_income: 0,
      cpa_pv: 0,
      rs_pv: 0,
      pv: 0,
      growth_target: 0,
      growth: 0,
      active_users: 0,
      old_active_users: 0,
      acquired_active_users: 0,
      churn_rate: 0,
      ratio_between_acquired_to_ftd: 0,
      avg_net_rev_per_active_user: 0,
      net_revenue: 0,
      avg_rs_deal_p: 0,
      br_to_ng_active_rs_ratio: 0,
      epc_cpa: 0,
      epc_rs: 0,
      epc: 0,
      estNetRev: 0,
      estRs: 0,
      estCpaIncome: 0,
    };
  });

  // Combine and sort all months
  return [...mappedMonths, ...defaultMonths].sort((a: any, b: any) => {
    const monthIndexA = monthNames.indexOf(a.displayMonth);
    const monthIndexB = monthNames.indexOf(b.displayMonth);
    return monthIndexA - monthIndexB;
  });
};
